<template>
  <aside v-if="visible" :class="['local-mode-banner', `local-mode-banner--${state.toLowerCase()}`]">
    <div class="local-mode-banner__status">
      <i class="fa-solid fa-server" aria-hidden="true"></i>
      <div class="local-mode-banner__content">
        <strong>{{ title }}</strong>
        <span>{{ summary }}</span>
      </div>
    </div>
    <RouterLink v-if="authority.hubReachable" :to="recoveryActive ? '/SincronizacionLocal' : '/PuntoDeVenta'">
      {{ recoveryActive ? 'Ver recuperación' : 'Abrir TPV' }}
    </RouterLink>
  </aside>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

const store = useStore();
const route = useRoute();
const authority = computed(() => store.state.operationalMode);
const state = computed(() => authority.value.state);
// El estado local normal ya se muestra en el badge del header. Esta franja se
// reserva para transiciones o bloqueos que realmente exigen atención.
const visible = computed(() => [
  'PREPARING_LOCAL',
  'LOCAL_HUB_UNAVAILABLE',
  'RECOVERY_REQUIRED',
  'CLOUD_RETURN_PREPARING',
].includes(state.value) && !['Login', 'LoginAdmin', 'ChangePass'].includes(route.name) && !route.meta.adminOnly);
const recoveryActive = computed(() => Boolean(
  authority.value.recovery
  || String(state.value).startsWith('RECOVERY_')
  || state.value === 'CLOUD_RETURN_PREPARING',
));
const shortSession = computed(() => String(
  authority.value.session?.session_uuid || authority.value.session?.id || 'pendiente',
).slice(0, 8));
const summary = computed(() => {
  const cloud = authority.value.cloudReachable
    ? 'Cloud ha vuelto; recuperación pendiente'
    : 'Trabajando con el Local Hub';
  const commands = Number(authority.value.session?.local_commands_count || 0);
  return `${cloud} · sesión ${shortSession.value} · ${commands} comandos locales`;
});
const title = computed(() => {
  if (state.value === 'LOCAL_HUB_UNAVAILABLE') {
    return 'El servidor local no está disponible. La operativa está detenida para evitar inconsistencias.';
  }
  if (state.value === 'PREPARING_LOCAL') return 'Preparando el cambio coordinado a modo local.';
  if (state.value === 'RECOVERY_REQUIRED') return 'Modo local activo. Recuperación obligatoria pendiente.';
  if (state.value === 'CLOUD_RETURN_PREPARING') {
    return 'Retorno a cloud en preparación. Las acciones están bloqueadas durante el corte final.';
  }
  if (String(state.value).startsWith('RECOVERY_')) {
    return 'Modo local activo. EWU está reconciliando los datos con cloud.';
  }
  return 'Modo local activo. EWU funciona mediante el servidor del restaurante.';
});
</script>

<style scoped>
.local-mode-banner {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
  min-height: 52px;
  padding: 9px max(18px, calc((100vw - 1180px) / 2));
  box-sizing: border-box;
  border-bottom: 1px solid #f59e0b;
  background: #fffbeb;
  color: #78350f;
  pointer-events: none;
}
.local-mode-banner__status {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}
.local-mode-banner__status > i {
  flex: 0 0 auto;
  color: #d97706;
}
.local-mode-banner__content {
  display: grid;
  gap: 2px;
  min-width: 0;
}
.local-mode-banner strong {
  font-size: .86rem;
  line-height: 1.25;
}
.local-mode-banner span {
  overflow: hidden;
  font-size: .74rem;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.local-mode-banner a {
  flex: 0 0 auto;
  padding: 7px 12px;
  border-radius: 999px;
  background: #92400e;
  color: white;
  font-weight: 800;
  text-decoration: none;
  pointer-events: auto;
}
.local-mode-banner--local_hub_unavailable {
  border-color: #dc2626;
  background: #fef2f2;
  color: #991b1b;
}

@media (max-width: 640px) {
  .local-mode-banner {
    align-items: flex-start;
    padding: 10px 14px;
  }

  .local-mode-banner span {
    white-space: normal;
  }
}
</style>
